export interface ProtectedEntity {
  _id: string;
  type: number;
  name: string;
  specifics: Specifics;
  company: CompanyShort | null;
  assignedPolicy?: AssignedPolicy;
  activePolicy?: ActivePolicy;
  hasSecurityIssues?: boolean;
  troubleshooting?: Troubleshooting;
  parentId: string;
  path: string;
  isProtectedBySecurityContainer?: boolean;
  readOnly?: boolean;
}

export interface Specifics {
  fqdn?: string;
  operatingSystemVersion?: string;
  ip?: string;
  lastSync: Date;
  lastSeen?: Date;
  isOnline?: boolean;
  label?: string;
  operatingSystem?: number;
  isVirtualServer?: boolean;
  isVirtual?: boolean;
  uuid?: string;
  imageName?: string;
  endpointUsers?: EndpointUsers;
  clientRules?: Array<ClientRule>;
  clientPolicy?: ClientPolicy;
  canBeManagedByAbove?: boolean;
  serverHasNsxIntegration?: boolean;
  isGhostr?: boolean; // on-premise
  isNsva?: boolean; // on-premise
  productType?: LicenseProductType;
  isManagedByHvi?: boolean; // on-premise
  isBest?: boolean;
  isSandboxAnalyzer?: boolean;
  appId?: number;
  isExchangeServer?: boolean;
  canRunExchangeScanTasks?: boolean;
  isManaged?: boolean;
  isIsolated?: boolean;
  isSecurityContainer?: boolean;
  isGoldenImage?: boolean;
  rights?: CompanyRights;
  isSva?: boolean;
  isContainerHost?: boolean;
  isPatchManagementServer?: boolean;
  isSuperAgent?: boolean;
  isPowerUser?: boolean;
  pendingRestart?: boolean;
  pendingRestartForHvi?: boolean;
  pendingRestartPatchManagement?: boolean;
  stateCode?: PowerState;
  maStatus?: MAStatus;
  hasPatchManagement?: boolean;
  hasEventCorrelator?: boolean;
  isFratComputer?: boolean;
  endpointId?: string;
  gzTagIds?: Array<string>;
  hasIntegrityMonitor?: boolean;
  isAdSyncer?: boolean;
  accountId?: string;
  hasVulnerabilityAssessment?: boolean;
  licenses?: Array<License>;
  license?: License;
  inheritedLicense?: boolean;
  hasMspTrial?: boolean;
  movingInfo?: MovingInfo;
  isVmwareSyncer?: boolean;
  tags?: Array<IntegrationTag>;
  restorePath?: string;
}

export interface IntegrationTag {
  key: string;
  value?: string;
}

export interface MovingInfo {
  state: MovingState;
  destinationCompany: CompanyShort;
}

export enum MovingState {
  IN_PROGRESS = 1,
  SUCCESS = 2,
  FAILED = 3,
}

export enum LicenseType {
  TRIAL = 1,
  END_USER = 2,
  MONTHLY = 3,
}

export interface License {
  type: LicenseType;
}

export interface ADSyncerSpecifics extends Specifics {
  isAdSyncer: boolean;
  guid: string;
  ssid?: string;
  domainSid: string;
  samAccountName: string;
  domainId?: string;
  isAdIntegration?: boolean;
}

export interface CompanyRights {
  companyManager: boolean;
  manageCompanies: boolean;
  manageNetworks: boolean;
}

export interface Troubleshooting {
  state: number;
}

export enum PowerState {
  POWER_STATE_PENDING = 0,
  POWER_STATE_RUNNING = 16,
  POWER_STATE_SHUTTING_DOWN = 32,
  POWER_STATE_TERMINATED = 48,
  POWER_STATE_STOPPING = 64,
  POWER_STATE_STOPPED = 80,
}

export interface CompanyRights {
  companyManager: boolean;
  manageCompanies: boolean;
  manageNetworks: boolean;
}

export interface ClientPolicy {
  policyId?: string;
  policyName?: string;
}

export interface ClientRule {
  policy: PolicyInfo;
  rule: RuleInfo;
  appliedOn?: Date;
}

export interface PolicyInfo {
  id: string;
  name: string;
}

export interface RuleInfo {
  id: string;
  name: string;
  type: number;
}

export interface CompanyShort {
  name: string;
  id: string;
}

export interface AssignedPolicy {
  policyName: string;
  confirmed?: boolean;
  inheritedFrom?: InheritedFrom;
}

export interface InheritedFrom {
  id?: string;
  name?: string;
  type?: number;
}

export interface ActivePolicy {
  inheritedFrom?: InheritedFrom;
  confirmed?: boolean;
  policyName?: string;
  enforced?: boolean;
}

export enum OperatingSystemType {
  UNKNOWN = 0,
  IOS = 1,
  ANDROID = 2,
  WINDOWS = 3,
  LINUX = 4,
  SOLARIS = 5,
  MACOSX = 6,
  CONTAINER = 7,
}

export interface EndpointUsers {
  userNames: Array<string>;
  userCount: number;
  lastUpdate: Date;
}

export enum ProtectedEntityType {
  TYPE_COMPANY = 2,
  TYPE_COMPANIES_COLLECTION = 3,
  TYPE_COMPANY_NETWORK = 4,
  TYPE_COMPANY_FOLDER = 5,
  TYPE_ROOT = 256,
  TYPE_ACTIVE_DIRECTORY = 257,
  TYPE_CUSTOM_GROUPS = 258,
  TYPE_AD_COMPUTER_GROUP = 259,
  TYPE_AD_COMPUTER = 260,
  TYPE_CUSTOM_GROUP = 261,
  TYPE_EXCLUDED_FOLDER = 272,
  TYPE_COMPUTER = 262,
  TYPE_AWS_GROUP = 263,
  TYPE_AWS_REGION = 264,
  TYPE_AWS_INSTANCE = 265,
  TYPE_AWS_AZ = 273,
  TERMINATED_INSTANCES_GROUP = 274,
  TYPE_AWS_ACCOUNT = 275,
  TYPE_CONTAINER_COLLECTION = 800,
  TYPE_CONTAINER_CUSTOM_GROUPS = 801,
  TYPE_CONTAINER_FOLDER = 802,
  TYPE_CONTAINER_HOST = 803,
  TYPE_CONTAINER = 804,
}

export enum MAStatus {
  MUTUAL_AUTH_TYPE_UNVERIFIED = 1,
  MUTUAL_AUTH_TYPE_VERIFIED = 2,
  MUTUAL_AUTH_TYPE_PENDING = 3,
  MUTUAL_AUTH_TYPE_REJECTED = 4,
  MUTUAL_AUTH_TYPE_NA = 5,
}

//CommonConsole/app/lib/constants.js#2307
export enum LicenseProductType {
  DEFAULT = 0,
  COMPANION = 3,
}
